import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CONTACT_CALL_DISPLAY, CONTACT_EMAIL, getCallUrl } from '../../utils/contact';
import {
  AppBar,
  Toolbar,
  IconButton,
  Button,
  Box,
  Container,
  Typography,
  Drawer,
  List,
  ListItemText,
  ListItemButton,
  ListItemIcon,
  Popper,
  Grow,
  Paper,
  ClickAwayListener,
  MenuList,
  MenuItem,
  useMediaQuery,
  useTheme,
  Chip,
  alpha,
  Collapse,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Phone,
  Email,
  Close,
  Download,
  School,
  Home,
  Info,
  ContactMail,
  CurrencyRupee,
  LocationOn,
  AccessTime,
  ExpandMore,
  ExpandLess,
} from '@mui/icons-material';
const Header = ({ onEnquiryOpen }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [activeRoute, setActiveRoute] = useState('');
  const [mbbsMenuOpen, setMbbsMenuOpen] = useState(false);
  const [mbbsExpanded, setMbbsExpanded] = useState(false);
  const mbbsAnchorRef = React.useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('sm'));
  // Track current route for highlighting
  useEffect(() => {
    setActiveRoute(location.pathname);
  }, [location.pathname]);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Track active section based on scroll position (only on home page)
  useEffect(() => {
    // Must be in DOM order (top to bottom) for the active section detection to work correctly
    const sectionIds = ['home', 'mbbs', 'portfolio', 'about', 'fees', 'contact'];
    const handleScrollSection = () => {
      // Only track active section if we're on the home page where sections exist
      if (location.pathname !== '/') return;

      const scrollPosition = window.scrollY + 150;
      for (let i = sectionIds.length - 1; i >= 0; i--) {
        const element = document.getElementById(sectionIds[i]);
        if (element && element.offsetTop <= scrollPosition) {
          setActiveSection(sectionIds[i]);
          break;
        }
      }
    };
    window.addEventListener('scroll', handleScrollSection);
    return () => window.removeEventListener('scroll', handleScrollSection);
  }, [location.pathname]);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const headerOffset = 120;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }
  };

  const handleNavClick = (sectionId) => {
    setMobileOpen(false);
    if (location.pathname !== '/') {
      // Navigate to home page with state so SectionsPage can scroll on mount
      navigate('/', { state: { scrollTo: sectionId } });
    } else {
      scrollToSection(sectionId);
    }
  };

  const handleMbbsToggle = () => {
    setMbbsMenuOpen((prevOpen) => !prevOpen);
  };

  const handleMbbsClose = () => {
    setMbbsMenuOpen(false);
  };

  const handleMbbsSubItemClick = (item) => {
    if (item.popupRequired) {
      // Open the counselling popup first; on success, navigate to the path
      onEnquiryOpen(() => {
        if (item.path) {
          navigate(item.path);
        }
      });
    } else if (item.path) {
      navigate(item.path);
    } else if (item.sectionId) {
      // Navigate to home page with state so SectionsPage can scroll on mount
      navigate('/', { state: { scrollTo: item.sectionId } });
    }
    setMobileOpen(false);
    setMbbsMenuOpen(false);
    setMbbsExpanded(false);
  };

  const menuItems = [
    {
      label: 'Home',
      sectionId: 'home',
      icon: <Home sx={{ fontSize: 18 }} />
    },
    {
      label: 'MBBS',
      sectionId: 'mbbs',
      icon: <School sx={{ fontSize: 18 }} />,
      subItems: [
        { label: 'Fees Structure', path: '/fees-structure', icon: <CurrencyRupee sx={{ fontSize: 18 }} />, popupRequired: true },
      ],
    },
    {
      label: 'PORTFOLIO',
      sectionId: 'portfolio',
      icon: <School sx={{ fontSize: 18 }} />
    },
    {
      label: 'About',
      sectionId: 'about',
      icon: <Info sx={{ fontSize: 18 }} />
    },
    {
      label: 'Contact',
      sectionId: 'contact',
      icon: <ContactMail sx={{ fontSize: 18 }} />
    },
  ];

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const isActive = (sectionId) => {
    // Only highlight sections when on home page
    if (location.pathname !== '/') return false;
    return activeSection === sectionId;
  };

  const isMbbsActive = () => {
    // Active on home page when scrolling to mbbs, OR on the fees-structure route
    return (location.pathname === '/' && activeSection === 'mbbs') || activeRoute === '/fees-structure';
  };

  const handleBrochureDownload = () => {
    // Always open the counselling popup; brochure downloads on form submission
    onEnquiryOpen(() => {
      window.open('/Brochure.pdf', '_blank', 'noopener,noreferrer');
    });
  };

  const handleMbbsExpandToggle = () => {
    setMbbsExpanded((prev) => prev === true ? false : true);
  };

  // Mobile Drawer Content
  const drawer = (
    <Box sx={{ height: '100%', bgcolor: '#f8f9fa', display: 'flex', flexDirection: 'column' }}>
      {/* Mobile Drawer Header */}
      <Box
        sx={{
          p: 2.5,
          background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
          color: 'white',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Box
          component="img"
          src="/LearnRabbit.png"
          alt="Learn Rabbit Logo"
          sx={{ height: 45, width: 'auto', borderRadius: '8px', objectFit: 'contain' }}
        />
        {/* Quick Contact Info in Drawer Header */}
        <Box sx={{ display: 'flex', gap: 2.5, flexWrap: 'wrap', justifyContent: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Phone sx={{ fontSize: 14, color: '#D4A853' }} />
            <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
              {CONTACT_CALL_DISPLAY}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Email sx={{ fontSize: 14, color: '#D4A853' }} />
            <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.65rem' }}>
              {CONTACT_EMAIL}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Mobile Navigation */}
      <List sx={{ px: 2, pt: 2 }}>
        {menuItems.map((item) => (
          <Box key={item.label}>
            <ListItemButton
              onClick={() => {
                if (item.subItems) {
                  handleMbbsExpandToggle();
                } else {
                  handleNavClick(item.sectionId);
                }
              }}
              sx={{
                borderRadius: 2,
                mb: 0.5,
                bgcolor: (item.subItems ? isMbbsActive() : isActive(item.sectionId))
                  ? alpha(theme.palette.primary.main, 0.1)
                  : 'transparent',
                '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.05) },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{ fontWeight: (item.subItems ? isMbbsActive() : isActive(item.sectionId)) ? 700 : 500, fontSize: '0.95rem' }}
              />
              {item.subItems && (
                mbbsExpanded ? <ExpandLess /> : <ExpandMore />
              )}
            </ListItemButton>
            {item.subItems && (
              <Collapse in={mbbsExpanded} timeout="auto" unmountOnExit>
                <List sx={{ pl: 4 }}>
                  {item.subItems.map((subItem) => (
                    <ListItemButton
                      key={subItem.label}
                      onClick={() => handleMbbsSubItemClick(subItem)}
                      sx={{
                        borderRadius: 2,
                        mb: 0.5,
                        py: 1,
                        bgcolor: activeRoute === subItem.path
                          ? alpha(theme.palette.primary.main, 0.08)
                          : 'transparent',
                        '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.05) },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        {subItem.icon || <School sx={{ fontSize: 16, color: 'text.secondary' }} />}
                      </ListItemIcon>
                      <ListItemText
                        primary={subItem.label}
                        primaryTypographyProps={{
                          fontSize: '0.85rem',
                          color: activeRoute === subItem.path ? 'primary.main' : 'text.secondary',
                          fontWeight: activeRoute === subItem.path ? 700 : 500,
                        }}
                      />
                    </ListItemButton>
                  ))}
                </List>
              </Collapse>
            )}
          </Box>
        ))}
      </List>

      {/* Mobile CTA Buttons */}
      <Box sx={{ p: 3, mt: 'auto' }}>
        <Button
          variant="contained"
          fullWidth
          startIcon={<Download />}
          onClick={handleBrochureDownload}
          sx={{ mb: 1.5, py: 1.5, borderRadius: 2, fontWeight: 600 }}
        >
          Download Brochure
        </Button>
        <Button
          variant="outlined"
          fullWidth
          onClick={() => {
            setMobileOpen(false);
            onEnquiryOpen();
          }}
          sx={{ py: 1.5, borderRadius: 2, fontWeight: 600 }}
        >
          Enquire Now
        </Button>
      </Box>
    </Box>
  );

  return (
    <Box
      id="main-header"
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 1100,
      }}
    >
      {/* Top Bar - Premium */}
      <Box
        sx={{
          bgcolor: '#2f2522',
          color: 'white',
          py: { xs: 0.5, sm: 1 },
          borderBottom: '2px solid',
          borderColor: 'primary.main',
        }}
      >
        <Container maxWidth="xl">
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: { xs: 'wrap', md: 'nowrap' },
              gap: 0.5,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 3 } }}>
              <Chip
                label={isTablet ? "🎓 Admissions Open" : "🎓 Admissions Open 2026-27"}
                size="small"
                sx={{
                  bgcolor: alpha('#fff', 0.15),
                  color: 'white',
                  fontWeight: 600,
                  fontSize: { xs: '0.65rem', sm: '0.75rem' },
                  height: { xs: 22, sm: 24 },
                  backdropFilter: 'blur(10px)',
                  '& .MuiChip-label': { px: { xs: 0.75, sm: 1 } },
                }}
              />
              {!isTablet && (
                <Typography variant="caption" sx={{ opacity: 0.8, fontWeight: 500 }}>
                  Your Trusted Partner for Medical Education
                </Typography>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 3 }, alignItems: 'center', flexWrap: 'nowrap', flexShrink: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Phone sx={{ fontSize: { xs: 12, sm: 16 }, color: '#D4A853' }} />
                <Typography variant="caption" sx={{ fontWeight: 500, fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                  {CONTACT_CALL_DISPLAY}
                </Typography>
              </Box>
              {!isMobile && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Email sx={{ fontSize: { xs: 12, sm: 16 }, color: '#D4A853' }} />
                  <Typography variant="caption" sx={{ fontWeight: 500, fontSize: { xs: '0.6rem', sm: '0.75rem' } }}>
                    {CONTACT_EMAIL}
                  </Typography>
                </Box>
              )}
              {!isTablet && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <LocationOn sx={{ fontSize: 16, color: '#D4A853' }} />
                  <Typography variant="caption" sx={{ fontWeight: 500 }}>
                    New Delhi, India
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Main Navigation */}
      <AppBar
        position="relative"
        elevation={scrolled ? 4 : 0}
        sx={{
          bgcolor: 'white',
          color: 'text.primary',
          transition: 'all 0.3s ease',
          borderBottom: scrolled ? 'none' : '1px solid',
          borderColor: 'divider',
        }}
      >
        <Container maxWidth="xl">
          <Toolbar disableGutters sx={{ minHeight: { xs: 60, md: 80 } }}>
            {/* Logo - Premium Design */}
            <Box
              onClick={() => handleNavClick('home')}
              sx={{
                display: 'flex',
                alignItems: 'center',
                textDecoration: 'none',
                mr: { xs: 2, sm: 4, md: 6 },
                flexShrink: 0,
                cursor: 'pointer',
              }}
            >
              <Box
                component="img"
                src="/LearnRabbit.png"
                alt="Learn Rabbit Logo"
                sx={{
                  height: { xs: 40, sm: 50, md: 55 },
                  width: 'auto',
                  objectFit: 'contain',
                  border: "none",
                }}
              />
            </Box>

            {/* Desktop Menu - Centered */}
            {!isMobile && (
              <Box sx={{ flexGrow: 1, display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                {menuItems.map((item) =>
                  item.subItems ? (
                    <Box key={item.label}>
                      <Button
                        ref={mbbsAnchorRef}
                        onClick={handleMbbsToggle}
                        endIcon={mbbsMenuOpen ? <ExpandLess /> : <ExpandMore />}
                        sx={{
                          color: isMbbsActive() ? 'primary.main' : 'text.primary',
                          fontWeight: isMbbsActive() ? 700 : 600,
                          fontSize: '0.9rem',
                          px: 2.5,
                          py: 1,
                          borderRadius: 2,
                          position: 'relative',
                          '&::after': isMbbsActive()
                            ? {
                                content: '""',
                                position: 'absolute',
                                bottom: 8,
                                left: '50%',
                                transform: 'translateX(-50%)',
                                width: 20,
                                height: 3,
                                bgcolor: 'primary.main',
                                borderRadius: 1,
                              }
                            : {},
                          '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.05),
                          },
                        }}
                      >
                        {item.label}
                      </Button>
                      <Popper
                        open={mbbsMenuOpen}
                        anchorEl={mbbsAnchorRef.current}
                        role={undefined}
                        placement="bottom-start"
                        transition
                        disablePortal
                        style={{ zIndex: 1300 }}
                      >
                        {({ TransitionProps, placement }) => (
                          <Grow
                            {...TransitionProps}
                            style={{
                              transformOrigin:
                                placement === 'bottom-start' ? 'left top' : 'left bottom',
                            }}
                          >
                            <Paper elevation={8} sx={{ mt: 1, borderRadius: 2, minWidth: 220, overflow: 'hidden' }}>
                              <ClickAwayListener onClickAway={handleMbbsClose}>
                                <MenuList>
                                  {item.subItems.map((subItem) => (
                                    <MenuItem
                                      key={subItem.label}
                                      onClick={() => handleMbbsSubItemClick(subItem)}
                                      sx={{
                                        py: 1.5,
                                        px: 2.5,
                                        gap: 1.5,
                                        fontSize: '0.9rem',
                                        fontWeight: activeRoute === subItem.path ? 700 : 500,
                                        color: activeRoute === subItem.path ? 'primary.main' : 'text.primary',
                                        bgcolor: activeRoute === subItem.path ? alpha(theme.palette.primary.main, 0.06) : 'transparent',
                                        '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.06) },
                                      }}
                                    >
                                      {subItem.icon && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', color: 'primary.main' }}>
                                          {subItem.icon}
                                        </Box>
                                      )}
                                      {subItem.label}
                                    </MenuItem>
                                  ))}
                                </MenuList>
                              </ClickAwayListener>
                            </Paper>
                          </Grow>
                        )}
                      </Popper>
                    </Box>
                  ) : (
                    <Button
                      key={item.label}
                      onClick={() => handleNavClick(item.sectionId)}
                      sx={{
                        color: isActive(item.sectionId) ? 'primary.main' : 'text.primary',
                        fontWeight: isActive(item.sectionId) ? 700 : 600,
                        fontSize: '0.9rem',
                        px: 2.5,
                        py: 1,
                        borderRadius: 2,
                        position: 'relative',
                        '&::after': isActive(item.sectionId)
                          ? {
                              content: '""',
                              position: 'absolute',
                              bottom: 8,
                              left: '50%',
                              transform: 'translateX(-50%)',
                              width: 20,
                              height: 3,
                              bgcolor: 'primary.main',
                              borderRadius: 1,
                            }
                          : {},
                        '&:hover': {
                          bgcolor: alpha(theme.palette.primary.main, 0.05),
                        },
                      }}
                    >
                      {item.label}
                    </Button>
                  )
                )}
              </Box>
            )}

            {/* CTA Buttons - Desktop */}
            {!isMobile && (
              <Box sx={{ display: 'flex', gap: 1.5, flexShrink: 0 }}>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  startIcon={<Download />}
                  onClick={handleBrochureDownload}
                  sx={{
                    fontWeight: 700,
                    px: 2.5,
                    py: 1,
                    borderRadius: 2,
                    fontSize: '0.85rem',
                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.4)}`,
                    '&:hover': {
                      boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.6)}`,
                      transform: 'translateY(-1px)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  Brochure
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={onEnquiryOpen}
                  sx={{
                    fontWeight: 700,
                    px: 2.5,
                    py: 1,
                    borderRadius: 2,
                    fontSize: '0.85rem',
                    borderWidth: 2,
                    '&:hover': {
                      borderWidth: 2,
                      transform: 'translateY(-1px)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  Enquire Now
                </Button>
              </Box>
            )}

            {/* Mobile Menu Button */}
            {isMobile && (
              <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto' }}>
                <IconButton
                  color="inherit"
                  aria-label="open drawer"
                  edge="start"
                  onClick={handleDrawerToggle}
                  sx={{
                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                    '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.1) },
                    width: { xs: 36, sm: 40 },
                    height: { xs: 36, sm: 40 },
                  }}
                >
                  {mobileOpen ? <Close /> : <MenuIcon />}
                </IconButton>
              </Box>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: { xs: '85%', sm: 350 },
            maxWidth: 350,
            bgcolor: '#ffffff',
          },
        }}
      >
        {drawer}
      </Drawer>
    </Box>
  );
};

export default Header;